import { parseQuery } from '@calendly/http-tools'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { setGetParams } from 'actions/core/navigation'

import { URL_STATE_PREFIX } from './constants'
import { encodeState, getStack, removeFromStack, setStack } from './helpers'
import { useCurrentUrlScope } from './use_current_url_scope'

const ALLOWED_TYPES = ['string', 'number', 'boolean', 'undefined']

const isTypeAllowed = (value) => {
  const type = typeof value
  return ALLOWED_TYPES.includes(type)
}

const validatePaneProps = (props) => {
  if (props && typeof props !== 'object') {
    throw new Error('Props must be an object.')
  }
  if (props && Object.values(props).some((value) => !isTypeAllowed(value))) {
    throw new Error('Props must be an object with string, number, or boolean values only.')
  }
}

/**
 * Filter out Google OAuth-related parameters (state, code, scope) from an object
 * @param {Object} params - The parameters object
 * @returns {Object} - The filtered parameters object
 */
const filterOutOAuthParams = ({ state, code, scope, ...rest }) => ({
  state: null, code: null, scope: null, ...rest
})

/**
 * @typedef {Object} Panel
 * @property {string} name - the name of the panel
 * @property {string} state - the state of the panel in base64
 *
 * @typedef {Object} UseScopedUrlState
 * @property {Function} open - open a panel
 * @property {Function} openOrReplace - open or replaces a panel if the previous is the same type
 * @property {Function} close - close a panel
 * @property {Function} replace - replace the current panel
 * @property {Function} replaceAndClearStack - replace the current panel and clear the remaining stack
 * @property {boolean} isOpen - returns true if a panel is open
 * @property {Object} current - the current open panel
 *
 * @typedef {Object} CloseOptions
 * @property {boolean} [all] - close all panels with the prefix
 * @property {Panel} [rail] - the panel to close
 */

/**
 * Hook to manage global url scoped state
 * @param {string} prefix
 * @param {Object} routes
 * @returns {UseScopedUrlState}
 */
export const useScopedUrlState = (prefix = URL_STATE_PREFIX.RIGHT_RAIL, routes = {}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const currentUrlScope = useCurrentUrlScope(prefix)

  const currentName = currentUrlScope[prefix]
  const currentState = currentUrlScope[`${prefix}State`]
  const currentProps = currentUrlScope[`${prefix}Props`]

  const route = routes[currentName]
  const checkIfCurrentRouteCanBeClosed = useCallback(() => route?.canBeClosed?.() ?? true, [route])

  /**
   * Open a panel
   * @param {string} name - the name of the panel
   * @param {Object} [props] - the props to pass to the panel
   * @returns {Panel} - the panel name and state
   */
  const open = useCallback((name, props) => {
    if (!checkIfCurrentRouteCanBeClosed()) { return {} }

    if (prefix !== URL_STATE_PREFIX.SHAREABLE) {
      validatePaneProps(props)
    }

    const state = encodeState(props)
    setStack(prefix, (stack) => [{ name, state }, ...stack])

    const searchParams = parseQuery(history.location.search)
    const filteredParams = filterOutOAuthParams(searchParams)
    dispatch(setGetParams({ ...filteredParams, [prefix]: name, [`${prefix}State`]: state || undefined }))

    return { name, state }
  }, [prefix, dispatch, history, checkIfCurrentRouteCanBeClosed])

  /**
    * Close the panel
   * @param {CloseOptions} [options] - the options to close the panel
   * @returns {void}
   */
  const close = useCallback(({ all, rail } = {}) => {
    if (!checkIfCurrentRouteCanBeClosed()) { return }

    if (all) {
      setStack(prefix, [])
    } else {
      setStack(prefix, (stack) => (rail ? removeFromStack(rail, stack) : [...stack.slice(1)]))
    }

    const stack = getStack(prefix)

    // Always update URL to reflect current stack state - don't rely on history.goBack()
    // because the previous page might also have pane parameters
    const { name, state } = stack[0] || {}
    const searchParams = parseQuery(history.location.search)
    const filteredParams = filterOutOAuthParams(searchParams)
    dispatch(setGetParams({ ...filteredParams, [prefix]: name, [`${prefix}State`]: state }))
  }, [prefix, dispatch, history, checkIfCurrentRouteCanBeClosed])

  /**
   * Replace the current panel
   * @param {string} name - the name of the panel
   * @param {Object} [props] - the props to pass to the panel
   * @returns {Panel} - the panel name and state
   */
  const replace = useCallback((name, props) => {
    if (!checkIfCurrentRouteCanBeClosed()) { return {} }

    if (prefix !== URL_STATE_PREFIX.SHAREABLE) {
      validatePaneProps(props)
    }

    const state = encodeState(props)
    setStack(prefix, (stack) => [{ name, state }, ...stack.slice(1)])

    const searchParams = parseQuery(history.location.search)
    const filteredParams = filterOutOAuthParams(searchParams)
    dispatch(setGetParams({ ...filteredParams, [prefix]: name, [`${prefix}State`]: state || undefined }))

    return { name, state }
  }, [prefix, dispatch, history, checkIfCurrentRouteCanBeClosed])

  /**
   * Replace the current panel and clear the remaining stack.
   * @param {string} name - the name of the panel
   * @param {Object} [props] - the props to pass to the panel
   * @returns {Panel} - the panel name and state
   */
  const replaceAndClearStack = useCallback((name, props) => {
    if (!checkIfCurrentRouteCanBeClosed()) { return {} }

    if (prefix !== URL_STATE_PREFIX.SHAREABLE) {
      validatePaneProps(props)
    }

    const state = encodeState(props)
    setStack(prefix, [{ name, state }])

    const searchParams = parseQuery(history.location.search)
    const filteredParams = filterOutOAuthParams(searchParams)
    dispatch(setGetParams({ ...filteredParams, [prefix]: name, [`${prefix}State`]: state || undefined }))

    return { name, state }
  }, [prefix, dispatch, history, checkIfCurrentRouteCanBeClosed])

  /**
   * Open or replace a panel if a panel of the same type is already open
   * @param {string} name - the name of the panel
   * @param {Object} [props] - the props to pass to the panel
   * @returns {Panel} - the panel name and state
   */
  const openOrReplace = useCallback((name, props) => {
    if (currentName && currentName === name) {
      return replace(name, props)
    }
    return open(name, props)
  }, [currentName, open, replace])

  const clearStack = useCallback(() => {
    setStack(prefix, [])
  }, [prefix])

  return {
    open,
    openOrReplace,
    close,
    replace,
    replaceAndClearStack,
    clearStack,
    isOpen: !!currentName,
    current: currentName ? {
      [prefix]: currentName,
      [`${prefix}State`]: currentState,
      [`${prefix}Props`]: currentProps
    } : null
  }
}
